import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type CellContext } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';

import DeleteSystemComponentFormModal from 'src/features/systems/components/systemComponents/form/DeleteSystemComponentFormModal';
import UpdateSystemComponentFormModal from 'src/features/systems/components/systemComponents/form/UpdateSystemComponentFormModal';
import { isBackendServiceComponent } from 'src/features/systems/utils/constants';
import { systemsMessages } from 'src/features/systems/utils/messages';
import { SystemComponent } from 'src/features/systems/utils/types';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface CellComponentActionsProps<TData, TValue> extends CellContext<TData, TValue> {
    singleRowSelection: (rowId: string) => void;
    sidePanelName: string;
}

export default function CellComponentActions({
    sidePanelName,
    singleRowSelection,
    row,
}: CellComponentActionsProps<SystemComponent, unknown>) {
    const { formatMessage } = useIntl();
    const { openPanel } = useSidePanel();

    const isBackendManaged = isBackendServiceComponent(row.original.component);

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        singleRowSelection(row.id.toString());
        openPanel(sidePanelName);
    };

    return (
        <div className="flex flex-1 justify-end gap-1">
            <TooltipProvider>
                {!isBackendManaged && (
                    <>
                        <Tooltip>
                            <UpdateSystemComponentFormModal
                                selectedSystemComponent={row.original}
                                semantics={{ entryName: formatMessage(systemsMessages.component).toLowerCase() }}
                            >
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                    </Button>
                                </TooltipTrigger>
                            </UpdateSystemComponentFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.edit} />
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <DeleteSystemComponentFormModal
                                selectedSystemComponent={row.original}
                                semantics={{ entryName: formatMessage(systemsMessages.component).toLowerCase() }}
                            >
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                    </Button>
                                </TooltipTrigger>
                            </DeleteSystemComponentFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.delete} />
                            </TooltipContent>
                        </Tooltip>
                    </>
                )}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            onClick={handleClick}
                            size="icon"
                            variant="link"
                            className="cursor-pointer hover:bg-gray-100"
                        >
                            <FontAwesomeIcon icon={ICON_MAP.SIDE_PANEL} />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">{formatMessage(visitorMessages.sidePanel)}</TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
}
